import _regenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/regenerator.js";
import _objectSpread from "/usr/app/node_modules/@babel/runtime/helpers/esm/objectSpread2.js";
import _asyncToGenerator from "/usr/app/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _slicedToArray from "/usr/app/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import React from "react";
var __jsx = React.createElement;
import { createContext, useContext, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api__getDhamenFormData } from "@/pages/checkout/dhamen/api";
import useHelpers from "@hooks/useHelpers";
import { DHAMEN_STATUS, MAX_NON_SAUDI_FINANCING_AMOUNT, MAX_SAUDI_FINANCING_AMOUNT } from "@/pages/checkout/dhamen/constants";

/** types **/

/** context **/
var DhamenContext = /*#__PURE__*/createContext(null);
export function useDhamenContext() {
  var context = useContext(DhamenContext);
  if (!context) {
    throw new Error("useDhamenContext must be used within a DhamenContextProvider");
  }
  return context;
}

/** context provider **/
var DhamenContextProvider = function DhamenContextProvider(_ref) {
  var children = _ref.children;
  var _useParams = useParams(),
    uuid = _useParams.uuid;
  var _useSearchParams = useSearchParams(),
    _useSearchParams2 = _slicedToArray(_useSearchParams, 2),
    searchParams = _useSearchParams2[0],
    setSearchParams = _useSearchParams2[1];
  var _useHelpers = useHelpers(),
    navigateTo = _useHelpers.navigateTo;

  // Initialize activeTab from URL or default to "user-info-form"
  var getInitialTab = function getInitialTab() {
    var tabFromUrl = searchParams.get("tab");
    if (tabFromUrl === "finance-form" || tabFromUrl === "user-info-form") {
      return tabFromUrl;
    }
    return "user-info-form";
  };
  var _useState = useState({
      orderInfo: null,
      loading: true,
      error: null,
      uuid: uuid || "",
      financingAmount: 0,
      activeTab: getInitialTab(),
      showClosingModal: false,
      status: null,
      consent: false,
      isExceedLimit: false,
      exceedLimitType: null
    }),
    state = _useState[0],
    setState = _useState[1];
  var fetchOrderInfo = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee() {
      var response, initialFinancingAmount, urlStatus, finalStatus, _t;
      return _regenerator().w(function (_context) {
        while (1) switch (_context.p = _context.n) {
          case 0:
            if (uuid) {
              _context.n = 1;
              break;
            }
            setState(function (prev) {
              return _objectSpread(_objectSpread({}, prev), {}, {
                loading: false,
                error: "UUID is required"
              });
            });
            return _context.a(2);
          case 1:
            _context.p = 1;
            setState(function (prev) {
              return _objectSpread(_objectSpread({}, prev), {}, {
                loading: true,
                error: null
              });
            });
            _context.n = 2;
            return api__getDhamenFormData({
              order_uuid: uuid
            });
          case 2:
            response = _context.v;
            if (response.success && response.data) {
              initialFinancingAmount = parseFloat(response.data.order.pricing.remaining_price.replace(/,/g, "")); // Check if there's a status parameter in the URL
              urlStatus = searchParams.get("status");
              finalStatus = response.data.status; // Default to API status
              // If URL status is REVISED or NEW, use it instead of API status
              if (urlStatus === DHAMEN_STATUS.REVISED || urlStatus === DHAMEN_STATUS.NEW) {
                finalStatus = urlStatus;
              }
              setState(function (prev) {
                return _objectSpread(_objectSpread({}, prev), {}, {
                  orderInfo: response.data,
                  loading: false,
                  error: null,
                  financingAmount: initialFinancingAmount,
                  status: finalStatus
                });
              });
            } else {
              navigateTo("/?ref=dhamen-404");
              setState(function (prev) {
                return _objectSpread(_objectSpread({}, prev), {}, {
                  orderInfo: null,
                  loading: false,
                  error: response.errors || "Failed to fetch order information"
                });
              });
            }
            _context.n = 4;
            break;
          case 3:
            _context.p = 3;
            _t = _context.v;
            navigateTo("/?ref=dhamen-404");
            console.error("Error fetching Dhamen order info:", _t);
            setState(function (prev) {
              return _objectSpread(_objectSpread({}, prev), {}, {
                orderInfo: null,
                loading: false,
                error: _t instanceof Error ? _t.message : "An error occurred"
              });
            });
          case 4:
            return _context.a(2);
        }
      }, _callee, null, [[1, 3]]);
    }));
    return function fetchOrderInfo() {
      return _ref2.apply(this, arguments);
    };
  }();
  var refetchOrderInfo = /*#__PURE__*/function () {
    var _ref3 = _asyncToGenerator(/*#__PURE__*/_regenerator().m(function _callee2() {
      return _regenerator().w(function (_context2) {
        while (1) switch (_context2.n) {
          case 0:
            _context2.n = 1;
            return fetchOrderInfo();
          case 1:
            return _context2.a(2);
        }
      }, _callee2);
    }));
    return function refetchOrderInfo() {
      return _ref3.apply(this, arguments);
    };
  }();
  var clearError = function clearError() {
    setState(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, {
        error: null
      });
    });
  };

  // Configuration for minimum financing amount

  var calculateFinancingAmount = function calculateFinancingAmount(downPayment) {
    var _state$orderInfo, _state$orderInfo$orde;
    if (!((_state$orderInfo = state.orderInfo) !== null && _state$orderInfo !== void 0 && (_state$orderInfo$orde = _state$orderInfo.order) !== null && _state$orderInfo$orde !== void 0 && _state$orderInfo$orde.pricing)) return 0;
    var pricing = state.orderInfo.order.pricing;
    var totalPrice = parseFloat(pricing.total_price.replace(/,/g, ""));
    var arboon = parseFloat(pricing.arboon || "0");

    // Formula: total_price - (arboon + down_payment)
    return totalPrice - (arboon + downPayment);
  };
  var setFinancingAmount = function setFinancingAmount(amount) {
    setState(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, {
        financingAmount: amount
      });
    });
  };
  var updateFinancingDetails = function updateFinancingDetails(details) {
    setState(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, {
        orderInfo: prev.orderInfo ? _objectSpread(_objectSpread({}, prev.orderInfo), {}, {
          financing_details: _objectSpread(_objectSpread({}, prev.orderInfo.financing_details), details)
        }) : null
      });
    });
  };
  var updateCustomerData = function updateCustomerData(customerData) {
    setState(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, {
        orderInfo: prev.orderInfo ? _objectSpread(_objectSpread({}, prev.orderInfo), {}, {
          order: _objectSpread(_objectSpread({}, prev.orderInfo.order), {}, {
            customer: _objectSpread(_objectSpread({}, prev.orderInfo.order.customer), customerData)
          })
        }) : null
      });
    });
  };
  var updateRequestedFinancingData = function updateRequestedFinancingData(requestedData) {
    setState(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, {
        orderInfo: prev.orderInfo ? _objectSpread(_objectSpread({}, prev.orderInfo), {}, {
          financing_details: _objectSpread(_objectSpread({}, prev.orderInfo.financing_details), requestedData)
        }) : null
      });
    });
  };

  // Tab and modal management functions
  var setActiveTab = function setActiveTab(tab) {
    setState(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, {
        activeTab: tab
      });
    });

    // Update URL when tab changes
    setSearchParams(function (prevParams) {
      var newParams = new URLSearchParams(prevParams);
      newParams.set("tab", tab);
      return newParams;
    });
  };
  var showClosingModal = function showClosingModal() {
    setState(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, {
        showClosingModal: true
      });
    });
  };
  var hideClosingModal = function hideClosingModal() {
    setState(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, {
        showClosingModal: false
      });
    });
  };
  var toggleClosingModal = function toggleClosingModal() {
    setState(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, {
        showClosingModal: !prev.showClosingModal
      });
    });
  };
  var handleBackClick = function handleBackClick() {
    if (state.status === DHAMEN_STATUS.NEW || state.status === DHAMEN_STATUS.REVISED) {
      if (state.activeTab === "finance-form") {
        setActiveTab("user-info-form");
      } else {
        showClosingModal();
      }
    } else {
      navigateTo("/site/dhamen/".concat(state.orderInfo.uuid, "?tab=finance-form&status=").concat(DHAMEN_STATUS.REVISED));
      actions.setStatus(DHAMEN_STATUS.REVISED);
    }
  };
  var setStatus = function setStatus(status) {
    setState(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, {
        status: status
      });
    });
  };
  var updateConsent = function updateConsent(consent) {
    setState(function (prev) {
      return _objectSpread(_objectSpread({}, prev), {}, {
        consent: consent
      });
    });
  };
  var checkExceedLimit = function checkExceedLimit(idNumber, financingAmount) {
    if (!idNumber || !financingAmount) {
      setState(function (prev) {
        return _objectSpread(_objectSpread({}, prev), {}, {
          isExceedLimit: false,
          exceedLimitType: null
        });
      });
      return;
    }
    var isSaudi = idNumber.startsWith("1");
    var isNonSaudi = idNumber.startsWith("2");
    if (isSaudi && financingAmount > MAX_SAUDI_FINANCING_AMOUNT) {
      setState(function (prev) {
        return _objectSpread(_objectSpread({}, prev), {}, {
          isExceedLimit: true,
          exceedLimitType: "saudi"
        });
      });
    } else if (isNonSaudi && financingAmount > MAX_NON_SAUDI_FINANCING_AMOUNT) {
      setState(function (prev) {
        return _objectSpread(_objectSpread({}, prev), {}, {
          isExceedLimit: true,
          exceedLimitType: "non-saudi"
        });
      });
    } else {
      setState(function (prev) {
        return _objectSpread(_objectSpread({}, prev), {}, {
          isExceedLimit: false,
          exceedLimitType: null
        });
      });
    }
  };

  // Sync tab state with URL parameters (for browser back/forward navigation)
  useEffect(function () {
    var tabFromUrl = searchParams.get("tab");
    if (tabFromUrl === "finance-form" || tabFromUrl === "user-info-form") {
      if (state.activeTab !== tabFromUrl) {
        setState(function (prev) {
          return _objectSpread(_objectSpread({}, prev), {}, {
            activeTab: tabFromUrl
          });
        });
      }
    }
  }, [searchParams, state.activeTab]);

  // Fetch order info on mount and when UUID changes
  useEffect(function () {
    fetchOrderInfo();
  }, [uuid]);
  var actions = {
    refetchOrderInfo: refetchOrderInfo,
    clearError: clearError,
    setFinancingAmount: setFinancingAmount,
    calculateFinancingAmount: calculateFinancingAmount,
    updateFinancingDetails: updateFinancingDetails,
    updateCustomerData: updateCustomerData,
    updateRequestedFinancingData: updateRequestedFinancingData,
    setActiveTab: setActiveTab,
    showClosingModal: showClosingModal,
    hideClosingModal: hideClosingModal,
    toggleClosingModal: toggleClosingModal,
    handleBackClick: handleBackClick,
    setStatus: setStatus,
    updateConsent: updateConsent,
    checkExceedLimit: checkExceedLimit
  };
  var contextValue = {
    state: state,
    actions: actions
  };
  return /*#__PURE__*/React.createElement(DhamenContext.Provider, {
    value: contextValue
  }, children);
};
export default DhamenContextProvider;